import { defineStore } from 'pinia';
import { ref } from 'vue';
import { ZIndex } from '@primeuix/utils';
import { useUtilities } from '@apparatix/composables';
import { Elevation, type ElevationCategory, type PrimevueElevationCategory } from '@apparatix/constants/Elevation';

/**
 * This store provides a more or less direct hook into Primevue's internal z-index management system.
 * While this store can be used directly, there is a companion composable and Vue component located at
 * composables/useManagedZIndex.ts and library/ElevationManagedDiv.vue that provide easy wrapping around
 * the common use cases for hooking into this system, so check to see if those will meet your needs before
 * using this store directly
 */
export const useZIndexManager = defineStore('z-index-manager', () => {
  const { developerWarning } = useUtilities();

  const managedElements = ref<Map<string, HTMLElement>>(new Map());

  const getPrimevueElevationCategory = (category: ElevationCategory): PrimevueElevationCategory => {
    switch (category) {
      case 'confirm':
        return 'overlay';
      case 'dialog':
        return 'modal';
      case 'drawer':
        return 'modal';
      case 'toast':
        return 'modal';
      case 'menu':
        return 'menu';
      case 'modal':
        return 'modal';
      case 'overlay':
        return 'overlay';
      case 'tooltip':
        return 'tooltip';
      default:
        return 'modal';
    }
  };

  const getElementZIndex = (el: HTMLElement): number => {
    const idx = parseInt(el?.style?.zIndex, 10);

    return isNaN(idx) ? undefined : idx;
  };


  const allocateIndex = (category: ElevationCategory): [ticket: string, zIndex: number] => {
    const pvCategory = getPrimevueElevationCategory(category);

    const ticket = crypto.randomUUID();

    const el = document.createElement('div');

    ZIndex.set(pvCategory, el, Elevation[category]);

    managedElements.value.set(ticket, el);

    return [ticket, getElementZIndex(el)];
  };

  const releaseIndex = (ticket: string): void => {
    const el = managedElements.value.get(ticket);

    if (!el) {
      developerWarning(true, new Error(`No element found for ${ticket}. This is likely due to the index already being released`), 'throw');

      return;
    }

    ZIndex.clear(el);
    managedElements.value.delete(ticket);
  };

  const getCurrentHighestIndex = (): number => [...managedElements.value.values()]
    .map(el => getElementZIndex(el))
    .filter(idx => idx !== undefined)
    .sort((a, b) => a - b)
    .pop();

  return {
    allocateIndex,
    getCurrentHighestIndex,
    releaseIndex,
  };
});
